import { FC, useState } from "react";
import { IGroupMenu, IMenuItem } from "../../MenuList";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import useInstitution from "../../../../hooks/useInstitution";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { styles } from "../styles";
import { Span } from "../../../Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MenuGroupItem } from "./MenuGroupItem";
import IsGranted from "../../../../guards/IsGranted";
import MenuBadge from "./MenuBadge";

export const MenuItem: FC<{
  item: IMenuItem;
  openMenu: string;
  setOpen: any;
  setIsCompactMenu: (value: boolean) => void;
  setSelectedCompactMenuItem: (value: any) => void;
}> = ({
  openMenu,
  item,
  setOpen,
  setSelectedCompactMenuItem,
  setIsCompactMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user }: any = useAuth();
  const business: any = useInstitution();

  const [openCollapse, setOpenCollapse] = useState("");

  const toggleClick = (path: string) => {
    if (openMenu === path) {
      setOpen("");
    } else {
      setOpen(path);
    }
  };

  const isRightUser = (requiredUsers?: string[]) => {
    if (!requiredUsers) return true;
    return requiredUsers.includes(user?.type);
  };

  const handleClickLink = (menuItem: IMenuItem) => {
    navigate(menuItem.path);
  };

  const isActive = (path: string) => {
    return location?.pathname === path;
  };

  const handleSetCategorizedMenu = (menuItem: IMenuItem) => {
    setSelectedCompactMenuItem(menuItem);
    setIsCompactMenu(true);
  };

  // Render the "Upgrade" badge for Clients
  const renderClientsUpgradeBadge = () => {
    if (item.title !== "Clients") return null;
    if (business?.settings?.usage?.can_create_clients ?? true) return null;

    return (
      <Span
        sx={{
          marginLeft: "auto",
          backgroundColor: "#FEC90D",
          padding: "2px",
          fontSize: "9px",
          borderRadius: "3px",
        }}
      >
        Upgrade
      </Span>
    );
  };

  return (
    <>
      {!item.subMenu ? (
        // Simple menu item without submenu
        <ListItemButton
          sx={[
            styles.menuItemButton,
            { display: isRightUser(item.users) ? "flex" : "none" },
          ]}
          className={isActive(item.path) ? "active" : ""}
          onClick={() => {
            handleClickLink(item);
            setOpen("");
          }}
        >
          <ListItemIcon sx={styles.menuItemIcon}>
            <item.Icon />
          </ListItemIcon>
          <ListItemText sx={styles.menuItemText} primary={item.title} />
          <MenuBadge show={item.hasNewFeature} />
          {renderClientsUpgradeBadge()}
        </ListItemButton>
      ) : (
        // Menu item with submenu
        <>
          {item.categories?.length ? (
            // Categorized menu item
            <ListItemButton
              sx={[
                styles.menuItemButton,
                { display: isRightUser(item.users) ? "flex" : "none" },
              ]}
              className={isActive(item.path) ? "active" : ""}
              onClick={() => handleSetCategorizedMenu(item)}
            >
              <ListItemIcon sx={styles.menuItemIcon}>
                <item.Icon />
              </ListItemIcon>
              <ListItemText sx={styles.menuItemText} primary={item.title} />
              <MenuBadge show={item.hasNewFeature} />
              {renderClientsUpgradeBadge()}
            </ListItemButton>
          ) : (
            // Regular menu item with submenu
            <>
              <ListItemButton
                sx={[
                  styles.menuItemButton,
                  { display: isRightUser(item.users) ? "flex" : "none" },
                ]}
                onClick={() => toggleClick(item.path)}
              >
                <ListItemIcon sx={styles.menuItemIcon}>
                  <item.Icon />
                </ListItemIcon>
                <ListItemText sx={styles.menuItemText} primary={item.title} />

                {/* Badges */}
                {renderClientsUpgradeBadge()}
                <MenuBadge show={item.hasNewFeature} />

                {/* Expand/collapse icon */}
                {openMenu === item.path ? (
                  <ExpandLessIcon sx={{ color: "#dfdfdf", fontSize: "16px" }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: "#dfdfdf", fontSize: "16px" }} />
                )}
              </ListItemButton>

              <Collapse
                sx={styles.collapseContainer}
                in={openMenu === item.path}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.subMenu?.map((subItem: IGroupMenu, index: number) => (
                    <IsGranted
                      key={`${subItem.title}sub-${index}`}
                      permissions={subItem?.permissions || []}
                      requiredUsers={subItem.users}
                    >
                      <MenuGroupItem
                        item={subItem}
                        openMenu={openCollapse}
                        setOpen={setOpenCollapse}
                      />
                    </IsGranted>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </>
      )}
    </>
  );
};
